import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { richText } from "./richText";

// ─── Types ────────────────────────────────────────────────────────────────────

interface GrammarTransition {
  from: number;
  to: number;
  letter: string;
}

interface GrammarDef {
  initial: number;
  final: number[];
  transitions: GrammarTransition[];
}

export interface GrammarIntroductionPage {
  title?: string;
  text?: string;
  instructions?: string[];
}

export interface GrammarTestIntroductionPage {
  title?: string;
  text?: string;
  instructions?: string[];
}

export interface GrammarConfig {
  letters?: string[];
  string_length_range?: [number, number];
  training_phase?: {
    strings_per_grammar: number;
    display_time_ms: number;
    isi_ms: number;
    introduction?: GrammarIntroductionPage[];
  };
  test_phase?: {
    total_trials: number;
    isi_ms: number;
    confidence_scale?: {
      min: number;
      max: number;
      label_min: string;
      label_max: string;
    };
    introduction?: GrammarTestIntroductionPage[];
  };
  grammars?: Record<string, GrammarDef>;
}

export interface GrammarTrialResult {
  trial_number: number;
  string_shown: string;
  correct_grammar: string;
  user_response: string;
  is_correct: boolean;
  confidence: number;
  response_time_ms: number;
}

export interface GrammarTaskAnswer {
  training_phase: {
    strings_shown: Array<{ grammar: string; string: string }>;
  };
  test_phase: {
    correct_count: number;
    error_count: number;
    trials: GrammarTrialResult[];
  };
}

interface Props {
  onAnswerChange: (taskAnswer?: GrammarTaskAnswer) => void;
  grammarConfig?: Partial<GrammarConfig>;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_CONFIG: {
  letters: string[];
  string_length_range: [number, number];
  training_phase: { strings_per_grammar: number; display_time_ms: number; isi_ms: number };
  test_phase: {
    total_trials: number;
    isi_ms: number;
    confidence_scale: { min: number; max: number; label_min: string; label_max: string };
  };
  grammars: Record<string, GrammarDef>;
} = {
  letters: ["M", "T", "V", "R", "X"],
  string_length_range: [5, 9],
  training_phase: {
    strings_per_grammar: 6,
    display_time_ms: 2500,
    isi_ms: 1000,
  },
  test_phase: {
    total_trials: 10,
    isi_ms: 1000,
    confidence_scale: {
      min: 1,
      max: 5,
      label_min: "Chute completo",
      label_max: "Certeza total",
    },
  },
  grammars: {
    G1: {
      initial: 0,
      final: [3],
      transitions: [
        { from: 0, to: 1, letter: "M" },
        { from: 0, to: 1, letter: "X" },
        { from: 1, to: 1, letter: "X" },
        { from: 1, to: 2, letter: "R" },
        { from: 1, to: 2, letter: "V" },
        { from: 2, to: 1, letter: "V" },
        { from: 2, to: 3, letter: "M" },
        { from: 2, to: 3, letter: "T" },
        { from: 3, to: 3, letter: "M" },
      ],
    },
    G2: {
      initial: 0,
      final: [3],
      transitions: [
        { from: 0, to: 1, letter: "X" },
        { from: 0, to: 1, letter: "M" },
        { from: 0, to: 1, letter: "V" },
        { from: 1, to: 1, letter: "R" },
        { from: 1, to: 2, letter: "V" },
        { from: 1, to: 2, letter: "T" },
        { from: 2, to: 1, letter: "T" },
        { from: 2, to: 3, letter: "R" },
        { from: 2, to: 3, letter: "M" },
        { from: 3, to: 3, letter: "R" },
      ],
    },
  },
};

// Teclas usadas para classificar a regra gramatical (1ª gramática = A, 2ª = L).
const RESPONSE_KEYS = ["A", "L"];

// Contagem exibida antes de iniciar cada fase de memorização e a fase de teste.
const START_COUNTDOWN_SECONDS = 3;

// "2500" → "2,5 segundos"; "5000" → "5 segundos".
const formatSeconds = (ms: number): string => {
  const seconds = ms / 1000;
  const label = Number.isInteger(seconds)
    ? String(seconds)
    : seconds.toFixed(1).replace(".", ",");
  return `${label} ${seconds === 1 ? "segundo" : "segundos"}`;
};

// A cópia vem das imagens de especificação, mas os valores que dependem da
// config — tempo de exibição, nomes das gramáticas, teclas de resposta e a
// escala de confiança — são resolvidos em runtime, para o texto não contradizer
// o comportamento real da tarefa.
const DEFAULT_GRAMMAR_INTRO_TITLE = "Tarefa de Gramática Artificial";
export const buildDefaultGrammarIntroductionPages = (
  cfg: typeof DEFAULT_CONFIG
): Array<Required<GrammarIntroductionPage>> => [
  {
    title: DEFAULT_GRAMMAR_INTRO_TITLE,
    text: "",
    instructions: [
      "<strong>Instrução geral:</strong> Nesta tarefa, você participará de um exercício de <strong>memória visual</strong> dividido em <strong>três fases.</strong> Diversas <strong>sequências de letras</strong> serão apresentadas, uma de cada vez, no centro da tela. Observe atentamente cada sequência e <strong>procure memorizar</strong> o maior número possível delas. Mantenha sua atenção durante toda a tarefa, pois cada sequência aparecerá " +
        `<strong>apenas uma vez, durante ${formatSeconds(cfg.training_phase.display_time_ms)}.</strong>`,
    ],
  },
];

const DEFAULT_GRAMMAR_TEST_INTRO_TITLE = "Tarefa";
export const buildDefaultGrammarTestIntroductionPages = (
  cfg: typeof DEFAULT_CONFIG
): Array<Required<GrammarTestIntroductionPage>> => {
  const grammarKeys = Object.keys(cfg.grammars);
  const g1 = grammarKeys[0] ?? "G1";
  const g2 = grammarKeys[1] ?? "G2";
  // Mesma associação usada nos botões de resposta: 1ª gramática → RESPONSE_KEYS[0].
  const key1 = RESPONSE_KEYS[0] ?? "A";
  const key2 = RESPONSE_KEYS[1] ?? "L";
  const scale = cfg.test_phase.confidence_scale;

  return [
    {
      title: DEFAULT_GRAMMAR_TEST_INTRO_TITLE,
      // A imagem traz 2 parágrafos, 3 marcadores e um parágrafo de fecho depois
      // da lista; o campo `text` é uma string única, então a estrutura vem como
      // HTML de bloco (o Typography desta tela renderiza com component="div").
      text:
        `<p>Na primeira fase de memorização, você observou diferentes sequências de letras que pertenciam à <strong>Regra Gramatical ${g1}</strong>. Na segunda fase de memorização, foram apresentadas novas sequências de letras que pertenciam à <strong>Regra Gramatical ${g2}</strong>. Durante essas fases, sua tarefa foi <strong>memorizar o máximo possível</strong> as sequências de letras apresentadas.</p>` +
        `<p>Agora, uma nova etapa será iniciada. <strong>Novas sequências de letras</strong> serão apresentadas no centro da tela, uma por vez. Sua tarefa será <strong>classificar cada sequência como pertencente à Regra Gramatical ${g1} ou à Regra Gramatical ${g2}</strong>, utilizando como referência as sequências que foram apresentadas anteriormente nas duas fases de memorização.</p>` +
        "<ul>" +
        `<li>Pressione a <strong>tecla ${key1}</strong> se você julga que a sequência de letras corresponde à <strong>Regra Gramatical ${g1}.</strong></li>` +
        `<li>Pressione a <strong>tecla ${key2}</strong> se você julga que a sequência de letras corresponde à <strong>Regra Gramatical ${g2}.</strong></li>` +
        `<li>Após classificar cada sequência, você deverá indicar o seu <strong>grau de confiança</strong> na resposta utilizando uma escala de <strong>${scale.min} (${scale.label_min}) a ${scale.max} (${scale.label_max}):</strong></li>` +
        "</ul>" +
        `<p>Lembre-se de utilizar como base as sequências apresentadas anteriormente. Primeiro, <strong>classifique</strong> a sequência como ${g1} ou ${g2} e, em seguida, <strong>informe</strong> o quanto você confia na sua resposta.</p>`,
      instructions: [],
    },
  ];
};

// ─── String generation ────────────────────────────────────────────────────────

function generateString(grammar: GrammarDef, minLen: number, maxLen: number): string | null {
  for (let attempt = 0; attempt < 500; attempt++) {
    let state = grammar.initial;
    let str = "";
    for (let step = 0; step < maxLen + 5; step++) {
      const currentState = state;
      const available = grammar.transitions.filter((t) => t.from === currentState);
      if (available.length === 0) break;
      let choices = available;
      if (str.length >= minLen) {
        const toFinal = available.filter((t) => grammar.final.includes(t.to));
        if (toFinal.length > 0 && Math.random() < 0.5) choices = toFinal;
      }
      const t = choices[Math.floor(Math.random() * choices.length)];
      str += t.letter;
      state = t.to;
      if (grammar.final.includes(state) && str.length >= minLen && str.length <= maxLen) return str;
      if (str.length >= maxLen) break;
    }
  }
  return null;
}

function generateStrings(grammar: GrammarDef, count: number, minLen: number, maxLen: number): string[] {
  const results = new Set<string>();
  let attempts = 0;
  while (results.size < count && attempts < 5000) {
    attempts++;
    const s = generateString(grammar, minLen, maxLen);
    if (s) results.add(s);
  }
  return Array.from(results).slice(0, count);
}

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function resolveConfig(raw?: Partial<GrammarConfig>): typeof DEFAULT_CONFIG {
  if (!raw) return DEFAULT_CONFIG;
  return {
    letters: raw.letters ?? DEFAULT_CONFIG.letters,
    string_length_range: raw.string_length_range ?? DEFAULT_CONFIG.string_length_range,
    training_phase: raw.training_phase ?? DEFAULT_CONFIG.training_phase,
    test_phase: {
      ...DEFAULT_CONFIG.test_phase,
      ...raw.test_phase,
      confidence_scale:
        raw.test_phase?.confidence_scale ?? DEFAULT_CONFIG.test_phase.confidence_scale,
    },
    grammars: raw.grammars ?? DEFAULT_CONFIG.grammars,
  };
}

// ─── Screen type ──────────────────────────────────────────────────────────────

type Screen =
  | "intro"
  | "training_isi"
  | "training"
  | "training_pause"
  | "test_intro"
  | "test_isi"
  | "test"
  | "confidence"
  | "done";

// ─── Component ────────────────────────────────────────────────────────────────

const GrammarQuestion: React.FC<Props> = ({ onAnswerChange, grammarConfig }) => {
  const cfg = React.useMemo(() => resolveConfig(grammarConfig), [grammarConfig]);

  const introductionPages = React.useMemo<Array<Required<GrammarIntroductionPage>>>(() => {
    const raw = grammarConfig?.training_phase?.introduction;
    // Aceita o formato atual (array de páginas) e o legado (objeto único { title, text, instructions }).
    const pages = Array.isArray(raw)
      ? raw
      : raw && typeof raw === "object"
        ? [raw as GrammarIntroductionPage]
        : null;

    if (!pages || pages.length === 0) return buildDefaultGrammarIntroductionPages(cfg);

    return pages.map((page) => ({
      title: page?.title ?? DEFAULT_GRAMMAR_INTRO_TITLE,
      text: page?.text ?? "",
      instructions: Array.isArray(page?.instructions) ? page.instructions : [],
    }));
  }, [grammarConfig, cfg]);

  const testIntroductionPages = React.useMemo<Array<Required<GrammarTestIntroductionPage>>>(() => {
    const raw = grammarConfig?.test_phase?.introduction;
    // Aceita o formato atual (array de páginas) e o legado (objeto único { title, text, instructions }).
    const pages = Array.isArray(raw)
      ? raw
      : raw && typeof raw === "object"
        ? [raw as GrammarTestIntroductionPage]
        : null;

    if (!pages || pages.length === 0) return buildDefaultGrammarTestIntroductionPages(cfg);

    return pages.map((page) => ({
      title: page?.title ?? DEFAULT_GRAMMAR_TEST_INTRO_TITLE,
      text: page?.text ?? "",
      instructions: Array.isArray(page?.instructions) ? page.instructions : [],
    }));
  }, [grammarConfig, cfg]);

  // Generated strings (populated on mount)
  const g1StringsRef = React.useRef<string[]>([]);
  const g2StringsRef = React.useRef<string[]>([]);
  const testTrialsRef = React.useRef<Array<{ string: string; grammar: string }>>([]);

  // UI display state
  const [screen, setScreen] = React.useState<Screen>("intro");
  const [introPageIndex, setIntroPageIndex] = React.useState(0);
  const [testIntroPageIndex, setTestIntroPageIndex] = React.useState(0);
  const [trainingGrammar, setTrainingGrammar] = React.useState("G1");
  // Totais de sequências: vêm do que foi realmente gerado (generateStrings pode
  // devolver menos que o pedido, já que exige sequências únicas), e não da
  // config crua — assim o contador nunca promete mais do que será exibido.
  const [trainingTotal, setTrainingTotal] = React.useState(0);
  const [trainingIdx, setTrainingIdx] = React.useState(0);
  const [testIdx, setTestIdx] = React.useState(0);
  const [testTotal, setTestTotal] = React.useState(0);
  const [displayString, setDisplayString] = React.useState("");
  const [progressPct, setProgressPct] = React.useState(1);
  const [userResponse, setUserResponse] = React.useState<string | null>(null);
  const [stringsReady, setStringsReady] = React.useState(false);
  const [finalAnswer, setFinalAnswer] = React.useState<GrammarTaskAnswer | undefined>();
  const [isStartingCountdown, setIsStartingCountdown] = React.useState(false);
  const [countdownValue, setCountdownValue] = React.useState(0);
  // Evita disparo duplo da contagem (botão + barra de espaço).
  const isCountingDownRef = React.useRef(false);

  // Accumulated results
  const stringsShownRef = React.useRef<Array<{ grammar: string; string: string }>>([]);
  const trialsRef = React.useRef<GrammarTrialResult[]>([]);
  const pendingTrialRef = React.useRef<{
    string: string;
    grammar: string;
    response: string;
    correct: boolean;
    responseTimeMs: number;
  } | null>(null);
  const trialStartRef = React.useRef(0);
  const testIdxRef = React.useRef(0);

  // Timers
  const timersRef = React.useRef<number[]>([]);
  const clearTimers = React.useCallback(() => {
    timersRef.current.forEach((id) => {
      clearTimeout(id);
      clearInterval(id);
    });
    timersRef.current = [];
  }, []);
  const addTimer = React.useCallback(
    (id: number) => {
      timersRef.current.push(id);
    },
    []
  );

  React.useEffect(() => () => clearTimers(), [clearTimers]);

  // Initialize strings on mount
  React.useEffect(() => {
    const [minLen, maxLen] = cfg.string_length_range;
    const grammarKeys = Object.keys(cfg.grammars);

    // Training strings: strings_per_grammar per grammar
    const trainingByGrammar: Record<string, string[]> = {};
    grammarKeys.forEach((key) => {
      trainingByGrammar[key] = generateStrings(
        cfg.grammars[key],
        cfg.training_phase.strings_per_grammar,
        minLen,
        maxLen
      );
    });

    // Test strings: total_trials distributed evenly across grammars
    const trialsPerGrammar = Math.ceil(cfg.test_phase.total_trials / grammarKeys.length);
    const testCombined: Array<{ string: string; grammar: string }> = [];
    grammarKeys.forEach((key) => {
      const strings = generateStrings(cfg.grammars[key], trialsPerGrammar, minLen, maxLen);
      strings.forEach((s) => testCombined.push({ string: s, grammar: key }));
    });
    const testTrials = shuffleArray(testCombined).slice(0, cfg.test_phase.total_trials);

    const g1Key = grammarKeys[0] ?? "G1";
    const g2Key = grammarKeys[1] ?? "G2";
    g1StringsRef.current = trainingByGrammar[g1Key] ?? [];
    g2StringsRef.current = trainingByGrammar[g2Key] ?? [];
    testTrialsRef.current = testTrials;
    setTrainingTotal(trainingByGrammar[g1Key]?.length ?? 0);
    setTestTotal(testTrials.length);
    setStringsReady(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Training flow ──────────────────────────────────────────────────────────

  const startTrainingStringRef = React.useRef<
    (grammar: string, idx: number, strings: string[]) => void
  >(() => { });

  const startTrainingString = React.useCallback(
    (grammar: string, idx: number, strings: string[]) => {
      clearTimers();
      if (idx >= strings.length) {
        const grammarKeys = Object.keys(cfg.grammars);
        const isFirstGrammar = grammar === (grammarKeys[0] ?? "G1");
        if (isFirstGrammar) {
          setScreen("training_pause");
        } else {
          setTestIntroPageIndex(0);
          setScreen("test_intro");
        }
        return;
      }

      const str = strings[idx];
      stringsShownRef.current.push({ grammar, string: str });
      setTrainingGrammar(grammar);
      setTrainingIdx(idx);
      // Cada gramática tem sua própria contagem de sequências.
      setTrainingTotal(strings.length);
      setDisplayString(str);
      setProgressPct(1);
      setScreen("training");

      // Progress bar interval
      const startTime = performance.now();
      addTimer(
        window.setInterval(() => {
          const pct = Math.max(
            0,
            1 - (performance.now() - startTime) / cfg.training_phase.display_time_ms
          );
          setProgressPct(pct);
        }, 50) as unknown as number
      );

      // Advance after display_time_ms → ISI → next string
      addTimer(
        window.setTimeout(() => {
          clearTimers();
          setScreen("training_isi");
          addTimer(
            window.setTimeout(() => {
              const grammarKeys = Object.keys(cfg.grammars);
              const nextStrings =
                grammar === (grammarKeys[0] ?? "G1")
                  ? g1StringsRef.current
                  : g2StringsRef.current;
              startTrainingStringRef.current(grammar, idx + 1, nextStrings);
            }, cfg.training_phase.isi_ms) as unknown as number
          );
        }, cfg.training_phase.display_time_ms) as unknown as number
      );
    },
    [cfg.grammars, cfg.training_phase, clearTimers, addTimer]
  );

  startTrainingStringRef.current = startTrainingString;

  // ── Test flow ──────────────────────────────────────────────────────────────

  const startTestTrialRef = React.useRef<(idx: number) => void>(() => { });

  const startTestTrial = React.useCallback(
    (idx: number) => {
      clearTimers();
      testIdxRef.current = idx;
      setTestIdx(idx);
      setScreen("test_isi");
      addTimer(
        window.setTimeout(() => {
          const trial = testTrialsRef.current[idx];
          setDisplayString(trial.string);
          trialStartRef.current = performance.now();
          setScreen("test");
        }, cfg.test_phase.isi_ms) as unknown as number
      );
    },
    [cfg.test_phase.isi_ms, clearTimers, addTimer]
  );

  startTestTrialRef.current = startTestTrial;

  // ── Handlers ──────────────────────────────────────────────────────────────

  // Exibe "Começando em N..." e só então roda a ação — usado no início das duas
  // fases de memorização e da fase de classificação.
  const runWithStartCountdown = React.useCallback(
    (action: () => void) => {
      if (isCountingDownRef.current) return;
      clearTimers();
      isCountingDownRef.current = true;
      setIsStartingCountdown(true);
      setCountdownValue(START_COUNTDOWN_SECONDS);

      const tick = (remaining: number) => {
        if (remaining <= 0) {
          isCountingDownRef.current = false;
          setIsStartingCountdown(false);
          setCountdownValue(0);
          action();
          return;
        }
        setCountdownValue(remaining);
        addTimer(window.setTimeout(() => tick(remaining - 1), 1000) as unknown as number);
      };

      addTimer(window.setTimeout(() => tick(START_COUNTDOWN_SECONDS - 1), 1000) as unknown as number);
    },
    [clearTimers, addTimer]
  );

  const handleStartTraining = React.useCallback(() => {
    const grammarKeys = Object.keys(cfg.grammars);
    const firstKey = grammarKeys[0] ?? "G1";
    startTrainingStringRef.current(firstKey, 0, g1StringsRef.current);
  }, [cfg.grammars]);

  const advanceIntro = React.useCallback(() => {
    if (isCountingDownRef.current) return;
    if (introPageIndex < introductionPages.length - 1) {
      setIntroPageIndex((prev) => prev + 1);
      return;
    }
    if (!stringsReady) return;
    runWithStartCountdown(handleStartTraining);
  }, [introPageIndex, introductionPages.length, stringsReady, runWithStartCountdown, handleStartTraining]);

  const handleContinueToSecondGrammar = React.useCallback(() => {
    runWithStartCountdown(() => {
      const grammarKeys = Object.keys(cfg.grammars);
      const secondKey = grammarKeys[1] ?? "G2";
      startTrainingStringRef.current(secondKey, 0, g2StringsRef.current);
    });
  }, [cfg.grammars, runWithStartCountdown]);

  const handleStartTest = React.useCallback(() => {
    trialsRef.current = [];
    startTestTrialRef.current(0);
  }, []);

  const advanceTestIntro = React.useCallback(() => {
    if (isCountingDownRef.current) return;
    if (testIntroPageIndex < testIntroductionPages.length - 1) {
      setTestIntroPageIndex((prev) => prev + 1);
      return;
    }
    runWithStartCountdown(handleStartTest);
  }, [testIntroPageIndex, testIntroductionPages.length, runWithStartCountdown, handleStartTest]);

  const handleTestResponse = React.useCallback(
    (response: string) => {
      if (screen !== "test") return;
      const responseTimeMs = Math.round(performance.now() - trialStartRef.current);
      const trial = testTrialsRef.current[testIdxRef.current];
      const isCorrect = response === trial.grammar;
      setUserResponse(response);
      pendingTrialRef.current = {
        string: trial.string,
        grammar: trial.grammar,
        response,
        correct: isCorrect,
        responseTimeMs,
      };
      setScreen("confidence");
    },
    [screen]
  );

  const handleConfidence = React.useCallback(
    (confidence: number) => {
      const data = pendingTrialRef.current!;
      const trialNum = testIdxRef.current + 1;
      trialsRef.current.push({
        trial_number: trialNum,
        string_shown: data.string,
        correct_grammar: data.grammar,
        user_response: data.response,
        is_correct: data.correct,
        confidence,
        response_time_ms: data.responseTimeMs,
      });

      const nextIdx = testIdxRef.current + 1;
      if (nextIdx >= testTrialsRef.current.length) {
        const correctCount = trialsRef.current.filter((t) => t.is_correct).length;
        const errorCount = trialsRef.current.filter((t) => !t.is_correct).length;
        const answer: GrammarTaskAnswer = {
          training_phase: { strings_shown: [...stringsShownRef.current] },
          test_phase: {
            correct_count: correctCount,
            error_count: errorCount,
            trials: [...trialsRef.current],
          },
        };
        setFinalAnswer(answer);
        setScreen("done");
        onAnswerChange(answer);
      } else {
        startTestTrialRef.current(nextIdx);
      }
    },
    [onAnswerChange]
  );

  // Keyboard shortcuts
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (screen === "intro" && e.code === "Space") {
        e.preventDefault();
        advanceIntro();
      }
      if (screen === "training_pause" && e.code === "Space") {
        e.preventDefault();
        handleContinueToSecondGrammar();
      }
      if (screen === "test_intro" && e.code === "Space") {
        e.preventDefault();
        advanceTestIntro();
      }
      if (screen === "test") {
        const key = e.key.toUpperCase();
        if (key === "A") { e.preventDefault(); handleTestResponse("G1"); }
        if (key === "L") { e.preventDefault(); handleTestResponse("G2"); }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [screen, advanceIntro, advanceTestIntro, handleContinueToSecondGrammar, handleTestResponse]);

  // ── Derived ────────────────────────────────────────────────────────────────

  const grammarKeys = Object.keys(cfg.grammars);
  const safeIntroPageIndex = Math.min(introPageIndex, Math.max(0, introductionPages.length - 1));
  const currentIntroPage = introductionPages[safeIntroPageIndex];
  const isLastIntroPage = safeIntroPageIndex >= introductionPages.length - 1;
  const safeTestIntroPageIndex = Math.min(testIntroPageIndex, Math.max(0, testIntroductionPages.length - 1));
  const currentTestIntroPage = testIntroductionPages[safeTestIntroPageIndex];
  const isLastTestIntroPage = safeTestIntroPageIndex >= testIntroductionPages.length - 1;
  const confidenceScale =
    cfg.test_phase.confidence_scale ?? DEFAULT_CONFIG.test_phase.confidence_scale;
  const confidenceValues = Array.from(
    { length: confidenceScale.max - confidenceScale.min + 1 },
    (_, i) => i + confidenceScale.min
  );

  // ── Render ────────────────────────────────────────────────────────────────

  // Cabeçalho de fase: rótulo, indicador compacto de progresso ao lado dele e
  // contador à direita. O indicador é estreito e cinza de propósito — na
  // memorização quem precisa ser lida de imediato é a barra regressiva de
  // largura total, em primary, logo abaixo.
  const renderPhaseProgress = (label: string, current: number, total: number) => {
    const safeTotal = Math.max(0, total);
    const safeCurrent = safeTotal > 0 ? Math.min(Math.max(current, 1), safeTotal) : 0;
    const fill = safeTotal > 0 ? safeCurrent / safeTotal : 0;

    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: 1.5,
          rowGap: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography variant="body1" color="primary" fontWeight={700} noWrap>
            {label}
          </Typography>
          {safeTotal > 0 && (
            <Box
              sx={{
                width: { xs: 80, sm: 120 },
                height: 5,
                flexShrink: 0,
                backgroundColor: "grey.300",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${fill * 100}%`,
                  backgroundColor: "grey.800",
                  transition: "width 0.2s linear",
                }}
              />
            </Box>
          )}
        </Box>
        {safeTotal > 0 && (
          <Typography variant="body2" color="text.secondary" noWrap>
            Sequência {safeCurrent} de {safeTotal}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 760,
        boxSizing: "border-box",
        margin: "0 auto",
        mt: 4,
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        backgroundColor: "grey.100",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >

      {/* ── INTRO ─────────────────────────────────────────────────────────── */}
      {screen === "intro" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h4" color="primary" sx={{ textAlign: "center", fontWeight: 700 }}>
              {richText(currentIntroPage.title)}
            </Typography>
            {currentIntroPage.text ? (
              <Typography variant="body1" component="div" sx={{ textAlign: "center" }}>
                {richText(currentIntroPage.text)}
              </Typography>
            ) : null}
          </Box>

          {currentIntroPage.instructions.length > 0 && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box component="ul" sx={{ listStyleType: "disc", paddingLeft: "1.25rem", margin: 0 }}>
                {currentIntroPage.instructions.map((line: string, i: number) => (
                  <Box component="li" key={i} sx={{ marginBottom: "0.3rem" }}>
                    <Typography variant="body1" component="div" color="text.secondary">{richText(line)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            {isStartingCountdown ? (
              <Typography variant="h5" color="primary">
                {`Começando em ${countdownValue}...`}
              </Typography>
            ) : (
              <>
                <Button
                  variant="contained"
                  size="medium"
                  onClick={advanceIntro}
                  disabled={isLastIntroPage && !stringsReady}
                >
                  {isLastIntroPage ? "Iniciar tarefa" : "Continuar"}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Pressione o botão ou a barra de espaço para dar seguimento na tarefa.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* ── TRAINING (+ ISI) ──────────────────────────────────────────────── */}
      {/* O ISI compartilha a tela da memorização: cabeçalho, contador e moldura
          continuam no lugar e só a sequência sai de cena, trocada pelo ponto de
          fixação na mesma posição em que as letras aparecem. */}
      {(screen === "training" || screen === "training_isi") && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {renderPhaseProgress(
            `${trainingGrammar === (grammarKeys[0] ?? "G1") ? "Primeira" : "Segunda"} fase de memorização`,
            trainingIdx + 1,
            trainingTotal
          )}

          {/* Barra regressiva do tempo de exibição desta sequência. */}
          <Box sx={{ height: 4, backgroundColor: "grey.300", borderRadius: 2, overflow: "hidden" }}>
            {screen === "training" && (
              <Box
                sx={{
                  height: "100%",
                  width: `${progressPct * 100}%`,
                  backgroundColor: "primary.main",
                  transition: "width 0.05s linear",
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              minHeight: 220,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 3,
            }}
          >
            {screen === "training" ? (
              <Typography
                variant="h1"
                color="primary"
                sx={{ fontSize: { xs: "2.75rem", md: "4.5rem" }, letterSpacing: "0.25em", minHeight: "6rem", wordBreak: "break-word", textAlign: "center" }}
              >
                {displayString}
              </Typography>
            ) : (
              <Box
                sx={{
                  minHeight: "6rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "grey.400" }} />
              </Box>
            )}
            {/* visibility em vez de condicional: mantém a altura reservada. */}
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ visibility: screen === "training" ? "visible" : "hidden" }}
            >
              memorize esta sequência
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── TRAINING PAUSE ────────────────────────────────────────────────── */}
      {screen === "training_pause" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h5" color="primary" sx={{ textAlign: "center", fontWeight: 700 }}>
              Primeira fase de memorização concluída
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 2 }}>
              Mais algumas sequências de letras aparecerão na tela. Novamente, sua tarefa é{" "}
              <strong>observar e memorizar</strong> as sequências o máximo possível.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Você está pronto(a) para continuar? Se sim, aperte a{" "}
              <strong>barra de espaço</strong> ou clique em Continuar.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {isStartingCountdown ? (
              <Typography variant="h5" color="primary">
                {`Começando em ${countdownValue}...`}
              </Typography>
            ) : (
              <Button variant="contained" size="medium" onClick={handleContinueToSecondGrammar}>
                Continuar
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* ── TEST INTRO ────────────────────────────────────────────────────── */}
      {screen === "test_intro" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="h4" color="primary" sx={{ textAlign: "center", fontWeight: 700 }}>
              {richText(currentTestIntroPage.title)}
            </Typography>
            {currentTestIntroPage.text ? (
              // Alinhado à esquerda: esta tela é parágrafos + lista, como na
              // imagem de especificação.
              <Typography variant="body1" component="div" sx={{ textAlign: "left" }}>
                {richText(currentTestIntroPage.text)}
              </Typography>
            ) : null}
          </Box>

          {currentTestIntroPage.instructions.length > 0 && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box component="ul" sx={{ listStyleType: "disc", paddingLeft: "1.25rem", margin: 0 }}>
                {currentTestIntroPage.instructions.map((line: string, i: number) => (
                  <Box component="li" key={i} sx={{ marginBottom: "0.3rem" }}>
                    <Typography variant="body1" component="div" color="text.secondary">{richText(line)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            {isStartingCountdown ? (
              <Typography variant="h5" color="primary">
                {`Começando em ${countdownValue}...`}
              </Typography>
            ) : (
              <>
                <Button variant="contained" size="medium" onClick={advanceTestIntro}>
                  {isLastTestIntroPage ? "Começar" : "Continuar"}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Pressione o botão ou a barra de espaço para dar seguimento.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* ── TEST (+ ISI) ──────────────────────────────────────────────────── */}
      {/* Mesma ideia da memorização: no ISI o cabeçalho e a moldura ficam, e o
          conteúdo interno é escondido por visibility para não deslocar nada —
          além de bloquear cliques nos botões antes da sequência aparecer. */}
      {(screen === "test" || screen === "test_isi") && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {renderPhaseProgress("Fase de classificação", testIdx + 1, testTotal)}

          <Box
            sx={{
              minHeight: 220,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 3,
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ visibility: screen === "test" ? "visible" : "hidden" }}
            >
              Qual é a regra gramatical desta sequência?
            </Typography>
            {screen === "test" ? (
              <Typography
                variant="h1"
                color="text.primary"
                sx={{ fontSize: { xs: "2.75rem", md: "4.5rem" }, letterSpacing: "0.25em", minHeight: "6rem", wordBreak: "break-word", textAlign: "center" }}
              >
                {displayString}
              </Typography>
            ) : (
              <Box
                sx={{
                  minHeight: "6rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "grey.400" }} />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                visibility: screen === "test" ? "visible" : "hidden",
              }}
            >
              {grammarKeys.map((g, i) => (
                <Button
                  key={g}
                  variant="outlined"
                  size="large"
                  onClick={() => handleTestResponse(g)}
                  sx={{ fontSize: 20, px: 5, py: 1.5, fontWeight: 700, minWidth: 100 }}
                >
                  {g}
                  <Typography variant="body2" color="text.disabled" sx={{ ml: 1 }}>
                    [{RESPONSE_KEYS[i] ?? i + 1}]
                  </Typography>
                </Button>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      {/* ── CONFIDENCE ────────────────────────────────────────────────────── */}
      {screen === "confidence" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Mesmo cabeçalho da tela de classificação: a confiança faz parte da
              mesma sequência, então o contador não deve sumir e voltar. */}
          {renderPhaseProgress("Fase de classificação", testIdx + 1, testTotal)}
          <Typography variant="h5" color="primary" sx={{ textAlign: "center" }}>
            Avalie sua confiança
          </Typography>

          <Box
            sx={{
              backgroundColor: "grey.100",
              borderRadius: "6px",
              padding: { xs: "1rem 1rem", md: "1.25rem 2rem" },
              textAlign: "center",
              wordBreak: "break-word",
            }}
          >
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Você respondeu:{" "}
              <strong>{userResponse}</strong>
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
              {confidenceValues.map((c) => (
                <Button
                  key={c}
                  variant="outlined"
                  onClick={() => handleConfidence(c)}
                  sx={{
                    width: 50,
                    height: 50,
                    minWidth: 50,
                    margin: "0 3px",
                    border: "1px solid #1ea097 !important",
                  }}
                >
                  <Typography variant="h4" color="primary">
                    {c}
                  </Typography>
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                maxWidth: 300,
                mx: "auto",
              }}
            >
              <Typography variant="body2" color="text.disabled">
                {confidenceScale.label_min}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {confidenceScale.label_max}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* ── DONE ──────────────────────────────────────────────────────────── */}
      {screen === "done" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, textAlign: "center", alignItems: "center" }}>
          <Typography variant="h4" color="primary">
            Tarefa concluída
          </Typography>
          <Typography variant="h6">Acertos: {finalAnswer?.test_phase.correct_count ?? 0}</Typography>
          <Typography variant="h6">Erros: {finalAnswer?.test_phase.error_count ?? 0}</Typography>
          <Typography variant="body1" color="text.secondary">
            Clique em "Próximo" para continuar.
          </Typography>
        </Box>
      )}

    </Box>
  );
};

export default GrammarQuestion;
